/**
 * One requirement card inside the PhotosPanel grid.
 *
 * Handles the per-requirement upload flow (camera/file picker), the N/A
 * toggle (admin/pm only), and the collapsed/expanded thumbnail grid so a
 * requirement with 30 photos doesn't push every card off-screen.
 */
import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Camera, Check, Loader2, Ban, Lock, Link2 } from "lucide-react";
import PhotoThumb from "./PhotoThumb";
import { partitionImages, isFileOrPhotoDrag, isInternalPhotoDrag, PHOTO_DRAG_TYPE } from "@/lib/photoDrop";

const COLLAPSED_LIMIT = 6;

export default function PhotoReqCard({
  item,
  categoryName,
  photos,
  busy,
  projectId,
  canReview,
  onUpload,
  onMovePhoto,
  locked = false,
  allowVideo = false,
  onRemove,
  onNaChanged,
  onPreview,
  selectionMode = false,
  selectedIds,
  onToggleSelect,
}) {
  const inputId = `add-photo-${item.key}`;
  const done = item.done;
  const isNa = !!item.na;
  const naReason = item.na_reason || "";
  const [expanded, setExpanded] = useState(false);
  // Dragover state drives the dashed blue ring on the whole card so
  // desktop users see exactly which requirement they're about to drop
  // onto when a project has 30+ slots stacked next to each other.
  const [dragOver, setDragOver] = useState(false);
  const visiblePhotos = expanded ? photos : photos.slice(0, COLLAPSED_LIMIT);
  const hiddenCount = photos.length - visiblePhotos.length;

  const handleChange = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length) {
      onUpload(files, { category: categoryName, key: item.key, label: item.label });
    }
  };

  // ---- Desktop drag & drop ----
  // Image recognition is tolerant: HEIC exports from iPhones often
  // arrive with an empty MIME type, so `partitionImages` falls back
  // to filename extension before rejecting. Non-image MIME types
  // (PDF, video, text) are still rejected cleanly. Selection mode
  // and N/A'd slots short-circuit — dropping during a bulk-move
  // would be ambiguous, and uploads to N/A slots are 409'd
  // server-side.
  const dropEnabled = !isNa && !selectionMode && !busy && !locked;
  // Guard the dragenter counter — children fire enter/leave events
  // when the pointer crosses them, which would flicker the highlight
  // off and on if we tracked a single boolean. The counter trick is
  // the canonical fix.
  const dragDepth = useRef(0);

  const onDragEnter = (e) => {
    if (!dropEnabled) return;
    // Only react to actual file drags, not internal element drags
    // (e.g. the dnd-kit sortable items in EditTemplatesDialog).
    if (!isFileOrPhotoDrag(e)) return;
    e.preventDefault();
    dragDepth.current += 1;
    setDragOver(true);
  };
  const onDragOver = (e) => {
    if (!dropEnabled) return;
    if (!isFileOrPhotoDrag(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = isInternalPhotoDrag(e) ? "move" : "copy";
  };
  const onDragLeave = () => {
    if (!dropEnabled) return;
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragOver(false);
  };
  const onDrop = (e) => {
    if (!dropEnabled) return;
    e.preventDefault();
    // Stop here — the parent SectionDropzone also listens for drops
    // and would otherwise re-route this batch across the section's
    // other slots. The per-card drop wins because the user aimed at
    // a specific requirement.
    e.stopPropagation();
    dragDepth.current = 0;
    setDragOver(false);
    // A thumbnail dragged from this page = move the original, never upload a copy.
    if (isInternalPhotoDrag(e)) {
      const photoId = e.dataTransfer.getData(PHOTO_DRAG_TYPE);
      if (photoId) onMovePhoto?.(photoId, { category: categoryName, key: item.key, label: item.label });
      return;
    }
    const all = Array.from(e.dataTransfer?.files || []);
    const { images, skipped } = partitionImages(all, { allowVideo });
    if (all.length && !images.length) {
      toast.error(allowVideo ? "Only photos or videos can be dropped on this slot." : "Only image files can be dropped on a photo slot.");
      return;
    }
    if (skipped.length > 0) {
      toast.info(`Skipped ${skipped.length} non-image file${skipped.length === 1 ? "" : "s"}.`);
    }
    if (images.length) {
      onUpload(images, { category: categoryName, key: item.key, label: item.label });
    }
  };

  const toggleNa = async () => {
    try {
      if (isNa) {
        await api.delete(`/projects/${projectId}/na-mark/${encodeURIComponent(item.key)}`);
        toast.success(`Marked "${item.label}" as required again`);
      } else {
        const reason = window.prompt(
          `Mark "${item.label}" as Not Applicable for this project?\n\nOptional reason (recorded in the closeout package):`,
          ""
        );
        // If the user pressed Cancel, prompt returns null — abort silently.
        if (reason === null) return;
        await api.post(`/projects/${projectId}/na-mark`, {
          requirement_key: item.key,
          reason,
        });
        toast.success(`"${item.label}" marked Not Applicable`);
      }
      onNaChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update");
    }
  };

  // NA cards get a dashed slate border so they read as "intentionally
  // empty" instead of "still pending".
  const borderClass = isNa
    ? "border-slate-600 border-dashed bg-slate-950/60"
    : done
      ? "border-emerald-800 bg-emerald-950/20"
      : "border-slate-700 bg-slate-950";
  // Dragover overrides the baseline border with a solid blue ring +
  // tinted bg so it's unmistakable which slot will receive the drop.
  const effectiveBorder = dragOver
    ? "border-blue-400 border-2 bg-blue-950/30 ring-2 ring-blue-500/40"
    : borderClass;

  return (
    <div
      className={`relative rounded-md border p-3 transition-colors ${effectiveBorder}`}
      data-testid={`photo-req-${item.key}`}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {dragOver && (
        <div
          className="absolute inset-0 rounded-md bg-blue-950/40 border-2 border-dashed border-blue-400 flex items-center justify-center pointer-events-none z-10"
          data-testid={`photo-dropzone-overlay-${item.key}`}
        >
          <div className="text-blue-100 font-display font-bold text-xs tracking-wider uppercase bg-blue-900/80 px-3 py-1.5 rounded shadow">
            Drop to add to “{item.label}”
          </div>
        </div>
      )}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="text-sm font-medium leading-tight">
          {item.label}
          {item.optional && !isNa && (
            <span
              className="ml-2 inline-flex items-center text-[9px] tracking-wider uppercase text-sky-300 border border-sky-800 px-1.5 py-0.5 rounded"
              title="Take if applicable — doesn't block closeout"
              data-testid={`photo-optional-${item.key}`}
            >
              If applicable
            </span>
          )}
          {isNa && (
            <span className="ml-2 inline-flex items-center text-[9px] tracking-wider uppercase text-slate-400 border border-slate-600 px-1.5 py-0.5 rounded">
              N/A
            </span>
          )}
          {item.description && !isNa && (
            <div className="mt-1 text-[11px] text-slate-400 italic leading-snug font-normal">
              {item.description}
            </div>
          )}
          {item.linked?.length > 0 && (
            <div
              className="mt-1 inline-flex items-center gap-1 text-[10px] text-sky-300/90 font-normal leading-snug"
              title="One photo fills all of these slots"
              data-testid={`photo-linked-${item.key}`}
            >
              <Link2 className="w-3 h-3 shrink-0" aria-hidden="true" />
              Shared with {item.linked.map((lk) => `${lk.template_name || "other template"} › ${lk.category}`).join(", ")}
            </div>
          )}
        </div>
        {isNa ? (
          <Ban className="w-4 h-4 text-slate-500 shrink-0" />
        ) : done ? (
          <Check className="w-4 h-4 text-emerald-400 shrink-0" />
        ) : (
          <Camera className="w-4 h-4 text-slate-500 shrink-0" />
        )}
      </div>

      {isNa ? (
        <div className="min-h-[40px] text-xs text-slate-400 italic mb-2">
          {naReason ? `“${naReason}”` : "Marked not applicable for this project."}
        </div>
      ) : (
        <>
          <div
            className={`grid grid-cols-3 gap-1 mb-2 min-h-[40px] ${expanded ? "max-h-72 overflow-y-auto pr-1" : ""}`}
            data-testid={`photo-thumbs-${item.key}`}
          >
            {visiblePhotos.map((p) => (
              <PhotoThumb
                key={p.id}
                photo={p}
                canReview={canReview}
                onOpen={() => onPreview(p)}
                onRemove={() => onRemove(p.id)}
                selectionMode={selectionMode}
                selected={selectedIds ? selectedIds.has(p.id) : false}
                onToggleSelect={onToggleSelect}
              />
            ))}
          </div>
          {photos.length > COLLAPSED_LIMIT && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="w-full mb-2 text-[11px] font-display font-bold tracking-wider uppercase text-blue-300 hover:text-blue-100 border border-slate-700 rounded py-1 transition-colors"
              data-testid={`photo-expand-${item.key}`}
            >
              {expanded
                ? `Show fewer · hide ${photos.length - COLLAPSED_LIMIT}`
                : `Show all ${photos.length} photos (+${hiddenCount} more)`}
            </button>
          )}
        </>
      )}

      {!isNa && !locked && (
        <input
          id={inputId}
          type="file"
          accept={allowVideo ? "image/*,video/*" : "image/*"}
          multiple
          className="sr-only"
          onChange={handleChange}
          data-testid={`photo-camera-${item.key}`}
        />
      )}

      <div className="grid grid-cols-3 gap-1.5">
        {!isNa && locked && (
          <span
            className={`${canReview ? "col-span-2" : "col-span-3"} flex items-center justify-center text-xs font-medium h-9 w-full rounded-md border border-amber-800/60 bg-slate-900 text-amber-300/80 select-none`}
            data-testid={`photo-locked-${item.key}`}
            title="Complete the JSA to unlock photo uploads"
          >
            <Lock className="w-3.5 h-3.5 mr-1" /> JSA required
          </span>
        )}
        {!isNa && !locked && (
          <label
            htmlFor={inputId}
            aria-disabled={busy ? "true" : "false"}
            className={`${canReview ? "col-span-2" : "col-span-3"} flex items-center justify-center text-xs font-medium h-9 w-full rounded-md border border-slate-700 bg-slate-900 active:bg-slate-800 select-none cursor-pointer ${busy ? "opacity-60 pointer-events-none" : ""}`}
            data-testid={`photo-camera-btn-${item.key}`}
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin"/> : <Camera className="w-3.5 h-3.5 mr-1"/>}
            {allowVideo ? "Add Photo/Video" : "Add Photo"}
          </label>
        )}
        {/* N/A / Restore is Admin/PM only. Dropping a required photo
            from the customer deliverable — or bringing one back — is
            a management call, not a field one. Foremen can still see
            the N/A badge and reason above so they know why a card is
            greyed out. */}
        {canReview && (
          <button
            type="button"
            onClick={toggleNa}
            className={`${isNa ? "col-span-3" : ""} flex items-center justify-center text-xs font-medium h-9 rounded-md border bg-slate-900 active:bg-slate-800 select-none ${isNa ? "border-blue-700 text-blue-300 hover:bg-blue-950/30" : "border-slate-700 text-slate-300 hover:bg-slate-800"}`}
            data-testid={`photo-na-btn-${item.key}`}
          >
            <Ban className="w-3.5 h-3.5 mr-1" />
            {isNa ? "Restore" : "N/A"}
          </button>
        )}
      </div>
    </div>
  );
}
